import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Eye, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { listReports, openPdf, retryFailedJob } from "@/lib/api";

export default function FailedReportsModal({ open, onClose, jobId, onRetried }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const load = async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const r = await listReports(jobId, { status: "failed", only_qualifying: false });
      setRows(r);
    } catch (e) {
      console.warn("FailedReportsModal: load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line
  }, [open, jobId]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="failed-reports-modal"
        className="max-w-3xl border-2 border-black rounded-none p-0 bg-white"
      >
        <DialogHeader className="border-b-2 border-black p-5">
          <DialogTitle className="font-chivo text-2xl font-black tracking-tighter flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#E62E2E]" />
            Failed Reports
          </DialogTitle>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] opacity-70 mt-1">
            Files that couldn&rsquo;t be extracted. Open each PDF to review manually,
            or click <strong>Retry All</strong> to re-process transient errors.
          </p>
          {rows.length > 0 && (
            <div className="mt-3">
              <button
                data-testid="retry-all-failed-btn"
                disabled={retrying}
                onClick={async () => {
                  setRetrying(true);
                  try {
                    const res = await retryFailedJob(jobId);
                    toast.success(
                      res.retried
                        ? `Re-queued ${res.retried} failed report${res.retried === 1 ? "" : "s"}`
                        : "Nothing to retry."
                    );
                    onClose();
                    if (onRetried) onRetried();
                  } catch (e) {
                    toast.error("Retry failed: " + (e?.response?.data?.detail || e.message));
                  } finally {
                    setRetrying(false);
                  }
                }}
                className="inline-flex items-center gap-1 border-2 border-black bg-black text-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] disabled:opacity-50 hover:bg-zinc-800"
              >
                <RotateCcw className={"w-3.5 h-3.5" + (retrying ? " animate-spin" : "")} />
                {retrying ? "Retrying…" : `Retry all ${rows.length}`}
              </button>
            </div>
          )}
        </DialogHeader>

        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="font-mono text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="font-mono text-xs uppercase tracking-[0.2em] opacity-70">
              No failed reports.
            </div>
          ) : (
            <div className="border border-black">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-black bg-zinc-50 text-left font-mono text-[11px] uppercase tracking-[0.2em]">
                    <th className="py-2 px-3">File</th>
                    <th className="py-2 px-3">Error</th>
                    <th className="py-2 px-3 text-right">Review</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`failed-row-${r.id}`}
                      className="border-b border-zinc-200 align-top"
                    >
                      <td className="py-2 px-3 font-mono text-[12px] max-w-[280px] truncate">
                        {r.file_name}
                      </td>
                      <td className="py-2 px-3 font-mono text-[11px] text-red-700 max-w-[300px]">
                        {r.error || "Unknown error"}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <button
                          data-testid={`view-failed-${r.id}`}
                          onClick={() => openPdf(r.id)}
                          className="inline-flex items-center gap-1 border border-black bg-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-black hover:text-white"
                          title="Open the original PDF to review manually (free)"
                        >
                          <Eye className="w-3.5 h-3.5" /> View PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="border-t-2 border-black p-4 flex justify-between gap-2 items-center">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] opacity-70">
            {rows.length} failed
          </span>
          <button
            data-testid="failed-modal-close"
            onClick={onClose}
            className="border border-black bg-white px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-zinc-100"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
